import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { Upload, FileText, X } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export function UploadPlanogrammModal({ open, onClose, onSuccess, bereiche, locations, token }) {
  const [regalName, setRegalName] = useState('');
  const [titel, setTitel] = useState('');
  const [bereichId, setBereichId] = useState('none');
  const [selectedStations, setSelectedStations] = useState([]);
  const [gueltigAb, setGueltigAb] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const toggleStation = (id) => {
    setSelectedStations(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );
  };

  const selectAllStations = () => {
    if (selectedStations.length === locations.length) {
      setSelectedStations([]);
    } else {
      setSelectedStations(locations.map(l => l.id));
    }
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f && f.type !== 'application/pdf') {
      toast.error('Nur PDF-Dateien erlaubt');
      return;
    }
    if (f && f.size > 50 * 1024 * 1024) {
      toast.error('Datei zu gross (max. 50 MB)');
      return;
    }
    setFile(f || null);
  };

  const handleSubmit = async () => {
    if (!regalName.trim()) { toast.error('Regalname ist Pflicht'); return; }
    if (!titel.trim()) { toast.error('Titel ist Pflicht'); return; }
    if (selectedStations.length === 0) { toast.error('Mindestens eine Station auswaehlen'); return; }
    if (!file) { toast.error('PDF-Datei auswaehlen'); return; }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('regal_name', regalName.trim());
      formData.append('titel', titel.trim());
      formData.append('bereich_id', bereichId === 'none' ? '' : bereichId);
      formData.append('station_ids', JSON.stringify(selectedStations));
      if (gueltigAb) formData.append('gueltig_ab', gueltigAb);
      formData.append('pdf_file', file);

      await axios.post(`${API}/api/planogramm/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
        timeout: 120000,
      });

      toast.success('Planogramm erfolgreich hochgeladen');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload fehlgeschlagen');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] sm:max-w-xl max-h-[90dvh] overflow-y-auto" data-testid="upload-planogramm-modal" aria-describedby="upload-planogramm-description">
        <DialogHeader>
          <DialogTitle>Planogramm hochladen</DialogTitle>
          <p id="upload-planogramm-description" className="text-sm text-muted-foreground">PDF-Datei mit Regalzuweisung hochladen</p>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Regalname */}
          <div className="space-y-1.5">
            <Label htmlFor="regal_name">Regalname *</Label>
            <Input
              id="regal_name"
              placeholder="z.B. Regal 3 - Suessigkeiten"
              value={regalName}
              onChange={(e) => setRegalName(e.target.value)}
              data-testid="input-regal-name"
            />
            <p className="text-xs text-muted-foreground">
              Bei gleichem Regalnamen wird das alte Planogramm ersetzt.
            </p>
          </div>

          {/* Titel */}
          <div className="space-y-1.5">
            <Label htmlFor="titel">Titel *</Label>
            <Input
              id="titel"
              placeholder="z.B. Herbst/Winter 2026"
              value={titel}
              onChange={(e) => setTitel(e.target.value)}
              data-testid="input-titel"
            />
          </div>

          {/* Bereich */}
          <div className="space-y-1.5">
            <Label>Bereich (optional)</Label>
            <Select value={bereichId} onValueChange={setBereichId}>
              <SelectTrigger data-testid="select-bereich">
                <SelectValue placeholder="Kein Bereich" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Kein Bereich</SelectItem>
                {bereiche.map(b => (
                  <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Stationen */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label>Stationen *</Label>
              <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={selectAllStations}>
                {selectedStations.length === locations.length ? 'Keine' : 'Alle'} auswaehlen
              </Button>
            </div>
            <div className="border rounded-md p-3 max-h-40 overflow-y-auto space-y-2" data-testid="station-list">
              {locations.length === 0 && (
                <p className="text-sm text-muted-foreground">Keine Standorte vorhanden</p>
              )}
              {locations.map(loc => (
                <label key={loc.id} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={selectedStations.includes(loc.id)}
                    onCheckedChange={() => toggleStation(loc.id)}
                    data-testid={`station-checkbox-${loc.id}`}
                  />
                  <span className="text-sm">{loc.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Gueltig ab */}
          <div className="space-y-1.5">
            <Label htmlFor="gueltig_ab">Gueltig ab (optional)</Label>
            <Input
              id="gueltig_ab"
              type="date"
              value={gueltigAb}
              onChange={(e) => setGueltigAb(e.target.value)}
              data-testid="input-gueltig-ab"
            />
          </div>

          {/* PDF Upload */}
          <div className="space-y-1.5">
            <Label>PDF-Datei *</Label>
            {file ? (
              <div className="flex items-center gap-2 p-3 border rounded-md bg-muted/50">
                <FileText className="h-5 w-5 text-primary shrink-0" />
                <span className="text-sm truncate flex-1">{file.name}</span>
                <span className="text-xs text-muted-foreground shrink-0">
                  {(file.size / 1024 / 1024).toFixed(1)} MB
                </span>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setFile(null)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <label
                className="flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed rounded-md cursor-pointer hover:border-primary/50 hover:bg-accent/50 transition-colors"
                data-testid="pdf-dropzone"
              >
                <Upload className="h-8 w-8 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">PDF hier ablegen oder klicken</span>
                <span className="text-xs text-muted-foreground">Max. 50 MB</span>
                <input type="file" accept=".pdf" className="hidden" onChange={handleFileChange} />
              </label>
            )}
          </div>
        </div>

        <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={uploading} data-testid="cancel-upload-btn" className="w-full sm:w-auto">
            Abbrechen
          </Button>
          <Button onClick={handleSubmit} disabled={uploading} data-testid="submit-upload-btn" className="w-full sm:w-auto">
            {uploading ? 'Wird hochgeladen...' : 'Hochladen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
